import {
  type ComponentProps,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import { styled } from '@material-ui/core';

import type { AnyExtension } from '@tiptap/core';
import Placeholder from '@tiptap/extension-placeholder';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import classNames from 'classnames';
import { Markdown } from 'tiptap-markdown';

import { typesetClassName } from '@spotify/confidence-core-react';

import { SimpleCodeBlock } from './CodeBlock';
import { EditorProvider } from './Context';
import { LinkHoverToolbar, MarkdownLink } from './Link';
import { SimpleSelectionToolbar } from './SimpleSelectionToolbar';
import { getMarkdown } from './markdown-utils';

const TypesetEditorContent = ({
  className,
  ...props
}: ComponentProps<typeof EditorContent>) => (
  <EditorContent
    className={classNames(typesetClassName, className)}
    {...props}
  />
);

const StyledEditorContent = styled(TypesetEditorContent)(() => ({
  '& > .ProseMirror': {
    outline: 'none',
  },
  '& .ProseMirror p.is-editor-empty:first-child::before': {
    color: 'var(--essential-subdued)',
    content: 'attr(data-placeholder)',
    float: 'left',
    height: 0,
    pointerEvents: 'none',
  },
}));

const NO_EXTENSIONS: AnyExtension[] = [];

export type SimpleMarkdownEditorProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  'aria-labelledby'?: string;
  extensions?: AnyExtension[];
  children?: (editor: NonNullable<ReturnType<typeof useEditor>>) => ReactNode;
};

export const SimpleMarkdownEditor = ({
  value,
  onChange,
  placeholder = 'Write something...',
  disabled = false,
  className,
  'aria-labelledby': ariaLabelledBy,
  extensions = NO_EXTENSIONS,
  children,
}: SimpleMarkdownEditorProps) => {
  const isInternalChange = useRef(false);

  const editorExtensions = useMemo(
    () => [
      StarterKit.configure({
        codeBlock: false,
        link: false,
      }),
      MarkdownLink.configure({
        openOnClick: true,
      }),
      Markdown.configure({
        html: false,
        transformPastedText: true,
        transformCopiedText: true,
      }),
      Placeholder.configure({
        placeholder,
      }),
      SimpleCodeBlock,
      ...extensions,
    ],
    [extensions, placeholder],
  );

  const editor = useEditor({
    editorProps: {
      attributes: {
        role: 'textbox',
        ...(ariaLabelledBy && { 'aria-labelledby': ariaLabelledBy }),
      },
    },
    extensions: editorExtensions,
    content: value,
    editable: !disabled,
    onUpdate: ({ editor: currentEditor }) => {
      isInternalChange.current = true;
      onChange(getMarkdown(currentEditor));
    },
  });

  // Sync external value changes
  useEffect(() => {
    if (!editor) return;

    // Skip if this is an internal change
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }

    const currentMarkdown = getMarkdown(editor);
    if (value !== currentMarkdown) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [editor, value]);

  // Update editable state
  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled, false);
    }
  }, [editor, disabled]);

  if (!editor) {
    return null;
  }

  return (
    <EditorProvider editor={editor}>
      <StyledEditorContent editor={editor} className={className} />
      {!disabled && (
        <>
          <SimpleSelectionToolbar />
          <LinkHoverToolbar />
        </>
      )}
      {children?.(editor)}
    </EditorProvider>
  );
};
